
import WebsiteRepositoryInterface, { Website } from '@/services/Website/interfaces';
import { AxiosError } from 'axios';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { WPXFormValue } from '@/components/templates/WPXForm';
import CreateDNSZoneForm from '@/services/DNS/forms/CreateDNSZoneForm';
import DNSRepositoryInterface from '@/services/DNS/Interfaces';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import PageHeader from '@/components/layout/PageHeader.vue';

@Component({
  components: {
    PageHeader,
  },
  inject: [
    'dnsRepository',
    'serverRepository',
    'websiteRepository',
  ],
})
export default class CreateDns extends Vue {
  public readonly dnsRepository!: DNSRepositoryInterface;
  public readonly serverRepository!: ServerRepositoryInterface;
  public readonly websiteRepository!: WebsiteRepositoryInterface;

  public loading: boolean = true;

  public form: WPXFormValue<CreateDNSZoneForm> = new WPXFormValue(CreateDNSZoneForm);
  public serversMap: { [id: string]: ServerInterface } = {};

  public websiteSearchLoading: boolean = false;
  public _websiteSearch: string = '';
  public websites: Website[] = [];

  public async created(): Promise<void> {
    const servers = await this.serverRepository.getAllServers();
    this.serversMap = {};
    servers.forEach((server) => {
      this.serversMap[server._id] = server;
    });

    this.loading = false;
  }

  public serverHostname(serverId: string): string {
    if (!serverId) {
      return 'No server';
    }

    return this.serversMap[serverId]?.hostname || serverId;
  }

  public get selectedWebsite(): Website | undefined {
    return this.websites.find((website) => website.id === this.form.data.websiteId);
  }

  public get selectedServerPreview(): { hostname: string; ip: string } | null {
    const serverId = this.form.data.server;
    if (!serverId) {
      return null;
    }

    const server = this.serversMap[serverId];
    if (!server) {
      return {
        hostname: serverId,
        ip: '',
      };
    }

    const ip = server.config?.dns_ip
      || (server.config?.ips || []).find((value) => !!value)
      || '';

    return {
      hostname: server.hostname,
      ip,
    };
  }

  @Watch('form.data.websiteId')
  public onWebsiteChange(websiteId: number): void {
    if (!websiteId) {
      this.form.data.server = '';
      return;
    }

    const website = this.websites.find((item) => item.id === websiteId);
    this.form.data.server = website?.originServer || '';
  }

  public async addDnsZone(): Promise<void> {
    try {
      await this.dnsRepository.createDNSZone(this.form.data);
    } catch (e) {
      switch ((e as AxiosError)?.response?.status) {
        case 409:
          this.$errorDialog.show("DNS Zone for domain already exists.");
          break;
        default:
          this.$errorDialog.show((e as AxiosError)?.response?.data?.message || (e as Error).message);
          break;
      }
    }
  }

  public showOverwriteWarning(): void {
    if (this.form.data.overwrite) {
      this.$infoDialog.show('Overwriting the DNS Zone will remove the previous DNS Records.')
    }
  }

  public get websiteSearch(): string {
    return this._websiteSearch;
  }

  public set websiteSearch(val: string) {
    // Vuetify clears search-input on blur/close — ignore that so the selection stays visible.
    if (val == null) {
      return;
    }

    this._websiteSearch = val;

    if (val.length > 0 && val.length < 3) {
      return;
    }

    this.websiteSearchLoading = true;
    this.websiteRepository.searchWebsites(0, val).then((websitesResponse) => {
      const selected = this.selectedWebsite;
      this.websites = websitesResponse.Websites || [];

      if (selected && !this.websites.some((website) => website.id === selected.id)) {
        this.websites = [selected, ...this.websites];
      }

      this.websiteSearchLoading = false;
    }).catch(() => {
      this.websiteSearchLoading = false;
    });
  }
}
